<template>
    <div
        class="bo-cell-datetime"
        :class="{ 'is-form': isForm }"
    >
        <template v-if="!editable && simple">
            <span>{{ dateRangeString }}</span>
        </template>
        <date-picker
            v-else
            :id="id"
            :value="dateRange"
            :editable="editable"
            :disabled="!editable"
            range
            confirm
            value-type="date"
            type="date"
            :range-separator="separator"
            :format="format"
            :confirmText="generalTranslations.apply ?? 'Apply'"
            :placeholder="placeholder"
            :disabled-date="disableDates"
            @change="changeHandler"
            :lang="datePickerLocale"
        ></date-picker>
    </div>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import moment from "moment";
import { dateEquals } from "@/v2/utils/date.js";

export default {
    components: { DatePicker },
    props: {
        id: {
            type: String,
            default: "",
        },
        start: {
            type: Date,
        },
        end: {
            type: Date,
        },
        minStartDate: {
            type: String,
            default: "0", // no limit
        },
        editable: {
            type: Boolean,
            default: false,
        },
        format: {
            type: String,
            default: window.bo?.dateFormat ?? "DD MMM YYYY",
        },
        momentLocale: {
            type: String,
            default: window.bo?.generalTranslations?.momentLocale ?? "en",
        },
        separator: {
            type: String,
            default: " -> ",
        },
        defaultStartText: {
            type: String,
            default: null,
        },
        defaultEndText: {
            type: String,
            default: null,
        },
        defaultEmptyText: {
            type: String,
            default: null,
        },
        //If either start or end dates are null, then dont show the separator
        showSeparatorIfEmpty: {
            type: Boolean,
            default: true,
        },
        //If the end date includes the whole day, then +1 day to the end date
        includeEndDay: {
            type: Boolean,
            default: true,
        },
        //Whether to show non editable dates as plain text
        simple: {
            type: Boolean,
            default: false,
        },
        isForm: {
            type: Boolean,
            default: false,
        },
        showNullIfEmpty: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            generalTranslations: window.bo?.generalTranslations ?? {},
            datePickerLocale: {
                formatLocale: {
                    months: window.bo?.generalTranslations?.date_range_months ?? [
                        "January",
                        "February",
                        "March",
                        "April",
                        "May",
                        "June",
                        "July",
                        "August",
                        "September",
                        "October",
                        "November",
                        "December",
                    ],
                    monthsShort: window.bo?.generalTranslations?.date_range_monthsShort ?? [
                        "Jan",
                        "Feb",
                        "Mar",
                        "Apr",
                        "May",
                        "Jun",
                        "Jul",
                        "Aug",
                        "Sep",
                        "Oct",
                        "Nov",
                        "Dec",
                    ],
                    weekdays: window.bo?.generalTranslations?.date_range_weekdays ?? [
                        "Sunday",
                        "Monday",
                        "Tuesday",
                        "Wednesday",
                        "Thursday",
                        "Friday",
                        "Saturday",
                    ],
                    weekdaysShort: window.bo?.generalTranslations?.date_range_weekdaysShort ?? [
                        "Sun",
                        "Mon",
                        "Tue",
                        "Wed",
                        "Thu",
                        "Fri",
                        "Sat",
                    ],
                    weekdaysMin: window.bo?.generalTranslations?.date_range_weekdaysMin ?? [
                        "Su",
                        "Mo",
                        "Tu",
                        "We",
                        "Th",
                        "Fr",
                        "Sa",
                    ],
                },
            },
        };
    },
    computed: {
        dateRange: {
            get() {
                return [this.start, this.endDate];
            },
            set(newValue) {
                if (this.includeEndDay && newValue[1]) {
                    newValue[1] = moment(newValue[1]).add(1, "d").toDate();
                }
                this.$emit("change", newValue);
                if (!dateEquals(newValue[0], this.start)) {
                    this.$emit("start_change", newValue[0]);
                }
                if (!dateEquals(newValue[1], this.end)) {
                    this.$emit("end_change", newValue[1]);
                }
            },
        },
        endDate() {
            if (this.includeEndDay && this.end) {
                return moment(this.end).add(-1, "d").toDate();
            }
            return this.end;
        },
        startText() {
            return this.defaultStartText ?? window.bo?.generalTranslations?.start ?? "Start";
        },
        endText() {
            return this.defaultEndText ?? window.bo?.generalTranslations?.end ?? "End";
        },
        emptyText() {
            return this.defaultEmptyText ?? "Select";
        },
        rangeSeparator() {
            // need separator if start & end have value
            if (this.start != null && this.end != null) {
                return this.separator;
            }

            if (this.showSeparatorIfEmpty === true && (this.start == null || this.end == null)) {
                return this.separator;
            }
            return " ";
        },
        dateRangeString() {
            if (this.showNullIfEmpty && this.start == null && this.end == null) {
                return "";
            } else {
                if (this.defaultEmptyText && this.start == null && this.end == null) {
                    return this.emptyText;
                }

                return `${this.getDateString(this.start, this.startText)}${this.rangeSeparator}${this.getDateString(
                    this.endDate,
                    this.endText
                )}`;
            }
        },
        placeholder() {
            return `${this.startText}${this.rangeSeparator}${this.endText}`;
        },
    },
    methods: {
        changeHandler(value) {
            this.dateRange = value;
        },
        getDateString(date, defaultString) {
            if (date == null) {
                return defaultString;
            }
            const newDate = moment(date)
                .locale(this.momentLocale)
                .format(this.format);
            return moment(newDate, this.format, this.momentLocale, true).isValid()
                ? newDate
                : "";
        },

        disableDates(date) {
            const selectedDate = new Date(date);
            selectedDate.setHours(0, 0, 0, 0);

            if ("today" == this.minStartDate) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                return selectedDate < today;
            }
            return false;
        },
    },
};
</script>

<style>
.mx-datepicker-range input {
    width: 100% !important;
    height: fit-content;
    box-shadow: none;
}

/* .mx-datepicker-range {
    width: 100% !important;
    max-width: 100% !important;
} */
</style>
